/**
 * Org Service — reglas PURAS (sin I/O), compartidas por el sync, el router, el panel
 * del manager y el front. Test: `npm run test:org` (CI). Tipos y catálogo de soluciones
 * en `shared/types/org.ts`.
 *
 * Lo que vive acá:
 *  - soluciones: a qué solución pertenece un nombre de la app de Carga de Horas o un
 *    área del Community Map;
 *  - períodos: mes en curso, qué meses relee el sync y cuándo una asignación está
 *    vigente ("mes en curso ∪ mes anterior hasta que el manager cierra el mes");
 *  - ids determinísticos de asignación;
 *  - resolución nombre de cliente → clientId del Brain (exacto → familia), reusando
 *    `normClient` / `clientFamily` / `isNonClient` de content-productivity;
 *  - estado derivado de una asignación que viene de la app (lock ⇒ confirmada; lo que
 *    confirmó o cerró un manager a mano no se pisa);
 *  - higiene: qué tiene que avisar el sync (id reusado, baja activa, manager fuera del
 *    Community Map, cliente sin resolver);
 *  - `planCapacitySync`: dado lo que dice la app para un mes y lo que ya hay en `org.assignment`,
 *    qué filas escribir, qué historial dejar y qué cerrar (puro: el conector solo lee y escribe).
 *
 * Nada de acá lee costos ni revenue: el servicio solo conoce persona, cliente,
 * solución, porcentaje, manager y lock.
 */
import { addMonthsYM, todayAR } from './dates';
import { normalizeEmail } from './identity';
import { foldKey } from './text-normalize';
import type { OrgAgentRejected } from './types/org';
import { clientFamily, isNonClient, normClient } from './content-productivity';
import {
  ORG_SOLUTIONS,
  type OrgAssignmentOrigin,
  type OrgAssignmentRow,
  type OrgAssignmentStatus,
  type OrgAssignmentUpsert,
  type OrgCloseReason,
  type OrgHistoryEvent,
  type OrgHygieneIssue,
  type OrgSolution,
  type OrgSyncStats,
} from './types/org';

export { clientFamily, isNonClient, normClient };

// ─── Soluciones ──────────────────────────────────────────────────────────────

/** Soluciones cuyas asignaciones salen de la app de Carga de Horas (sync diario). */
export const ALLOCATOR_SOLUTIONS: readonly OrgSolution[] = ORG_SOLUTIONS.filter((s) => s.source === 'allocator');

export function solutionBySlug(slug: string | null | undefined): OrgSolution | null {
  if (!slug) return null;
  return ORG_SOLUTIONS.find((s) => s.slug === slug) ?? null;
}

/** `solutions.name` de la app ("Content", "Paid Media Lab"…) → solución. */
export function solutionForAllocatorName(name: string | null | undefined): OrgSolution | null {
  if (!name) return null;
  const k = normClient(name);
  return ORG_SOLUTIONS.find((s) => s.allocatorName && normClient(s.allocatorName) === k) ?? null;
}

/**
 * Una solución a partir de un texto libre: slug exacto, nombre del allocator ("Content") o nombre
 * ("Creative Studio"); con `partial` también por contención ("owned" → Owned Media Lab, solo si
 * calza UNA: "growth" es ambiguo entre Growth Strategy y Business). Lo usan el agente (exacto) y el MCP (parcial).
 */
export function solutionByText(text: string, opts: { partial?: boolean } = {}): OrgSolution | null {
  const q = text.trim();
  if (!q) return null;
  const exact = solutionBySlug(q) ?? solutionForAllocatorName(q) ?? ORG_SOLUTIONS.find((s) => foldKey(s.name) === foldKey(q)) ?? null;
  if (exact || !opts.partial) return exact;
  const hits = ORG_SOLUTIONS.filter((s) => foldKey(s.name).includes(foldKey(q)));
  return hits.length === 1 ? hits[0] : null;
}

/**
 * Área del Community Map ("Creative Studio", "Paid Media Lab"…) → solución principal de la
 * persona. Primero por nombre exacto (`communityAreas`), después por patrón (`areaPatterns`,
 * la misma idea que `AREA_GROUP_RULES` de cognito-area-sync: "Business US/BR" o "Tech" caen
 * en su solución aunque People cambie el texto). Lo que no matchea es un aviso de higiene
 * (`area_unmapped`), nunca un error silencioso.
 */
export function solutionForArea(area: string | null | undefined): OrgSolution | null {
  if (!area) return null;
  const k = foldKey(area);
  if (!k) return null;
  const exact = ORG_SOLUTIONS.find((s) => s.communityAreas.some((a) => foldKey(a) === k));
  if (exact) return exact;
  return ORG_SOLUTIONS.find((s) => s.areaPatterns.some((p) => new RegExp(p).test(k))) ?? null;
}

// ─── Períodos y vigencia ─────────────────────────────────────────────────────

export const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** 'YYYY-MM' del día (hora de Buenos Aires). */
export function currentPeriod(today: string = todayAR()): string {
  return today.slice(0, 7);
}

/**
 * Qué meses relee el sync en cada corrida: el anterior y el en curso. La carga se completa
 * durante el mes y el cierre (lock) suele llegar en los primeros días del siguiente, así
 * que el mes anterior sigue cambiando hasta su lock.
 */
export function periodsToSync(today: string = todayAR()): [string, string] {
  const cur = currentPeriod(today);
  return [addMonthsYM(cur, -1), cur];
}

/**
 * Vigencia para ACCESOS: una asignación cuenta si es del mes en curso, o del mes anterior
 * mientras el manager todavía no cerró (lock) el mes en curso para ese cliente. Sin esa
 * regla los primeros días de cada mes todo el mundo perdería acceso hasta cargar horas.
 * `closed` nunca está vigente.
 */
export function isAssignmentCurrent(
  a: { period: string; status: OrgAssignmentStatus },
  opts: { today?: string; currentMonthLocked: boolean },
): boolean {
  if (a.status === 'closed') return false;
  const [prev, cur] = periodsToSync(opts.today ?? todayAR());
  if (a.period === cur) return true;
  if (a.period === prev) return !opts.currentMonthLocked;
  return false;
}

// ─── Ids ─────────────────────────────────────────────────────────────────────

/** Id determinístico de `org.assignment`: el mismo input siempre da el mismo id (sync idempotente). */
export function assignmentId(employeeId: number, clientId: string, solutionSlug: string, period: string): string {
  if (!Number.isInteger(employeeId) || employeeId <= 0) throw new Error(`assignmentId: employeeId inválido ${employeeId}`);
  if (!PERIOD_RE.test(period)) throw new Error(`assignmentId: período inválido ${period}`);
  return `${employeeId}:${clientId}:${solutionSlug}:${period}`;
}

// ─── Resolución de nombres de cliente ────────────────────────────────────────

export interface ClientNameRegistryEntry {
  id: string;
  displayName: string;
  capacityNames?: readonly string[];
  allocatorNames?: readonly string[];
}

export type ClientResolutionVia = 'allocator' | 'capacity' | 'display' | 'id' | 'family';
export interface ClientResolution {
  clientId: string;
  via: ClientResolutionVia;
}

/**
 * Índice nombre normalizado → clientId, armado una vez por corrida desde el registro de
 * brain-clients. Precedencia por lista: allocatorNames, capacityNames, displayName, id.
 * Que dos clientes no compartan un nombre lo garantiza `validateBrainClientConfigs` (CI).
 */
export function buildClientNameIndex(registry: readonly ClientNameRegistryEntry[]): Map<string, ClientResolution> {
  const index = new Map<string, ClientResolution>();
  const put = (name: string | undefined, clientId: string, via: ClientResolutionVia) => {
    if (!name) return;
    const k = normClient(name);
    if (k && !index.has(k)) index.set(k, { clientId, via });
  };
  for (const c of registry) for (const n of c.allocatorNames ?? []) put(n, c.id, 'allocator');
  for (const c of registry) for (const n of c.capacityNames ?? []) put(n, c.id, 'capacity');
  for (const c of registry) put(c.displayName, c.id, 'display');
  for (const c of registry) put(c.id, c.id, 'id');
  return index;
}

/**
 * Nombre de cliente tal cual lo escribe una fuente → clientId del Brain. Exacto primero;
 * si no, por familia ("KIMBERLY CLARK - HUGGIES" → "KIMBERLY CLARK"). Las filas que no son
 * clientes (SLACK - *, CAPACITY - *, PTO/OOO…) nunca resuelven.
 */
export function resolveClientId(name: string, index: Map<string, ClientResolution>): ClientResolution | null {
  if (!name || isNonClient(name)) return null;
  const k = normClient(name);
  const direct = index.get(k);
  if (direct) return direct;
  const fam = clientFamily(k);
  if (fam !== k) {
    const byFamily = index.get(fam);
    if (byFamily) return { clientId: byFamily.clientId, via: 'family' };
  }
  return null;
}

// ─── Estado derivado de una asignación que viene de la app ──────────────────

export interface ExistingAssignmentState {
  origin: OrgAssignmentOrigin;
  status: OrgAssignmentStatus;
  closed_reason?: OrgCloseReason | null;
}

/**
 * Qué estado queda cuando el sync trae una fila de la app para una asignación:
 *  - lock del cliente-mes ⇒ `confirmed` (el lock ES la confirmación del manager);
 *  - sin lock ⇒ `suggested`, salvo que ya estuviera confirmada (no se degrada);
 *  - lo que un manager cerró a mano en Internal (`closed_reason = 'manager'`) sigue cerrado
 *    aunque la app todavía la liste: el conflicto se ve en el panel, no se pisa.
 */
export function deriveCapacityStatus(existing: ExistingAssignmentState | null, locked: boolean): OrgAssignmentStatus {
  if (existing?.status === 'closed' && existing.closed_reason === 'manager') return 'closed';
  if (locked) return 'confirmed';
  if (existing?.status === 'confirmed') return 'confirmed';
  return 'suggested';
}

/**
 * Una asignación de origen `capacity` que ya no aparece en la app para un período que el
 * sync acaba de releer se cierra (`removed_from_source`). Las declaradas o inferidas no:
 * la app no es su fuente.
 */
export function shouldCloseMissing(existing: ExistingAssignmentState & { period: string }, syncedPeriods: readonly string[]): boolean {
  return existing.origin === 'capacity' && existing.status !== 'closed' && syncedPeriods.includes(existing.period);
}

// ─── Higiene ─────────────────────────────────────────────────────────────────

export interface AppMemberLite {
  employeeId: number;
  name: string;
  active: boolean;
  managerEmail?: string | null;
}

export interface CommunityPersonLite {
  employeeId: number;
  fullName: string;
  email: string;
  status: string;
}

/**
 * ¿Son dos formas de escribir a la misma persona? Todos los tokens (3+ letras, sin tildes ni
 * mayúsculas) del nombre más corto tienen que estar en el más largo: "Paula Endrizzi" ⊂
 * "MARIA PAULA ENDRIZZI" sí; "Ana García" vs "Ana Pérez" no (compartir el primer nombre no
 * alcanza: apagaría el aviso de employee_id reusado). No es `sameKey` (igualdad exacta) ni
 * la resolución de homónimos de community-index: solo sirve para la higiene del sync.
 */
export function sharesNameTokens(a: string, b: string): boolean {
  const toks = (s: string) => [...new Set(foldKey(s).split(/\s+/).filter((t) => t.length >= 3))];
  let ta = toks(a);
  let tb = toks(b);
  if (!ta.length || !tb.length) return false;
  if (ta.length > tb.length) [ta, tb] = [tb, ta];
  const longer = new Set(tb);
  return ta.every((t) => longer.has(t));
}

/**
 * Lo que el sync tiene que avisar (tab Salud + #alerts-bo), sin frenar la corrida:
 * un `employee_id` que en la app es otra persona que en el Community Map (id reusado),
 * gente activa en la app que ya es Baja, gente de la app que no existe en el Community
 * Map, managers con mail que no está en el Community Map, nombres de cliente sin resolver y
 * áreas del Community Map sin solución.
 */
export function hygieneIssues(input: {
  appMembers: readonly AppMemberLite[];
  community: readonly CommunityPersonLite[];
  unresolvedClients: readonly { name: string; persons: number }[];
  /** Áreas del Community Map que `solutionForArea` no resuelve (personas sin solución principal). */
  unmappedAreas?: readonly { area: string; persons: number }[];
}): OrgHygieneIssue[] {
  const issues: OrgHygieneIssue[] = [];
  const byId = new Map(input.community.map((p) => [p.employeeId, p]));
  const emails = new Set(input.community.map((p) => normalizeEmail(p.email)));
  const managersMissing = new Set<string>();
  for (const m of input.appMembers) {
    const cm = byId.get(m.employeeId);
    if (!cm) {
      if (m.active) issues.push({ kind: 'person_not_in_community', detail: `${m.name} (employee_id ${m.employeeId}) está activa en la app y no existe en el Community Map` });
    } else {
      if (!sharesNameTokens(m.name, cm.fullName)) issues.push({ kind: 'employee_id_reused', detail: `employee_id ${m.employeeId} es "${m.name}" en la app y "${cm.fullName}" en el Community Map` });
      else if (m.active && cm.status !== 'Activo') issues.push({ kind: 'active_in_app_but_baja', detail: `${cm.fullName} sigue activa en la app y en el Community Map está ${cm.status}` });
    }
    const mgr = normalizeEmail(m.managerEmail);
    if (mgr && !emails.has(mgr)) managersMissing.add(mgr);
  }
  for (const mgr of [...managersMissing].sort()) issues.push({ kind: 'manager_not_in_community', detail: `manager ${mgr} no está en el Community Map` });
  for (const c of input.unresolvedClients) issues.push({ kind: 'client_unresolved', detail: `"${c.name}" no resuelve a ningún cliente del Brain`, count: c.persons });
  for (const a of input.unmappedAreas ?? []) issues.push({ kind: 'area_unmapped', detail: `el área "${a.area}" del Community Map no cae en ninguna solución (ORG_SOLUTIONS)`, count: a.persons });
  return issues;
}

// ─── Plan del sync desde la app de Carga de Horas (puro) ────────────────────

/** Una fila de `allocations` de la app para un mes, ya con nombres (cliente, solución). */
export interface AllocatorAllocation {
  id: string;
  teamMemberId: string;
  /** `clients.id` de la app (para cruzar con los locks). */
  clientAppId: string;
  clientName: string;
  solutionName: string;
  pct: number | null;
  roleAtTime: string | null;
}

/** Un `team_members` de la app con el manager vigente del mes (overrides aplicados). */
export interface AllocatorMember {
  id: string;
  employeeId: number;
  name: string;
  active: boolean;
  roleTitle: string | null;
  managerEmail: string | null;
}

export interface CapacitySyncPlan {
  upserts: OrgAssignmentUpsert[];
  history: OrgHistoryEvent[];
  /** Asignaciones de origen capacity que la app ya no trae para este período. */
  closeIds: string[];
  /** Clientes del Brain lockeados en el período (todos sus nombres de la app con lock) → quién lockeó. */
  clientLocks: Map<string, string | null>;
  unresolvedClients: { name: string; persons: number }[];
  unresolvedSolutions: string[];
  /** Personas de la app con filas en el mes que no existen en el Community Map (sin employee_id conocido). */
  unmatchedPersons: number;
  stats: OrgSyncStats;
}

export const SYNC_ACTOR = 'sync:hour-allocator';

type ExistingAssignment = Pick<
  OrgAssignmentRow,
  'id' | 'origin' | 'status' | 'closed_reason' | 'role_title' | 'pct' | 'manager_email' | 'confirmed_by' | 'confirmed_at' | 'closed_at' | 'source_client_name'
>;

/**
 * Qué escribir en `org.assignment` para un mes a partir de las filas de la app.
 *
 * Reglas:
 *  - filas no-cliente afuera; nombre → clientId por `resolveClientId` (lo que no resuelve se
 *    cuenta por nombre, con cuántas personas, y queda en client_alias_pending);
 *  - dos nombres de la app que resuelven al mismo cliente del Brain (DIDI RIDES + DIDI FOOD →
 *    didi) se funden en UNA asignación: pct sumado (tope 100), referencias y nombres unidos,
 *    confirmada solo si TODOS esos clientes-mes tienen lock;
 *  - lock ⇒ confirmed (confirmado por el manager que lockeó); sin lock ⇒ suggested; lo que un
 *    manager confirmó o cerró a mano no se pisa (`deriveCapacityStatus`); una asignación
 *    declarada a mano que la app también trae conserva origen `declared`;
 *  - lo que estaba (origen capacity) y la app ya no trae se cierra con `removed_from_source`;
 *  - cada creación, cambio de campo o de estado deja una fila de historial con `SYNC_ACTOR`.
 */
export function planCapacitySync(input: {
  period: string;
  allocations: readonly AllocatorAllocation[];
  members: readonly AllocatorMember[];
  /** clientAppId → email de quien lockeó (null si no se sabe). Solo los clientes-mes lockeados. */
  locks: ReadonlyMap<string, string | null>;
  /** clientAppId → nombre en la app, para los clientes lockeados SIN filas este mes (igual cuentan como lockeados). */
  lockNames?: ReadonlyMap<string, string>;
  existing: readonly ExistingAssignment[];
  clientIndex: Map<string, ClientResolution>;
  /** employee_id que existen como persona (Community Map). */
  knownEmployeeIds: ReadonlySet<number>;
  /** ISO de la corrida (confirmed_at de lo que se confirma ahora). */
  now: string;
}): CapacitySyncPlan {
  const { period, now } = input;
  if (!PERIOD_RE.test(period)) throw new Error(`planCapacitySync: período inválido ${period}`);
  const memberById = new Map(input.members.map((m) => [m.id, m]));
  const existingById = new Map(input.existing.map((e) => [e.id, e]));

  type Draft = {
    employeeId: number;
    clientId: string;
    solutionSlug: string;
    roleTitle: string | null;
    pct: number;
    hasPct: boolean;
    managerEmail: string | null;
    refs: string[];
    names: string[];
    appClientIds: Set<string>;
  };
  const drafts = new Map<string, Draft>();
  /** Todos los nombres de la app que resuelven a cada cliente del Brain este mes (con o sin persona conocida). */
  const appIdsByClient = new Map<string, Set<string>>();
  const unresolved = new Map<string, Set<string>>();
  const unresolvedSolutions = new Set<string>();
  const unmatched = new Set<string>();

  for (const a of input.allocations) {
    if (isNonClient(a.clientName)) continue;
    const client = resolveClientId(a.clientName, input.clientIndex);
    if (client) {
      const ids = appIdsByClient.get(client.clientId) ?? new Set<string>();
      ids.add(a.clientAppId);
      appIdsByClient.set(client.clientId, ids);
    }
    const member = memberById.get(a.teamMemberId);
    if (!member || !input.knownEmployeeIds.has(member.employeeId)) {
      unmatched.add(a.teamMemberId);
      continue;
    }
    if (!client) {
      const set = unresolved.get(a.clientName) ?? new Set<string>();
      set.add(a.teamMemberId);
      unresolved.set(a.clientName, set);
      continue;
    }
    const solution = solutionForAllocatorName(a.solutionName);
    if (!solution) {
      unresolvedSolutions.add(a.solutionName);
      continue;
    }
    const id = assignmentId(member.employeeId, client.clientId, solution.slug, period);
    const d = drafts.get(id) ?? {
      employeeId: member.employeeId,
      clientId: client.clientId,
      solutionSlug: solution.slug,
      roleTitle: a.roleAtTime ?? member.roleTitle ?? null,
      pct: 0,
      hasPct: false,
      managerEmail: normalizeEmail(member.managerEmail) || null,
      refs: [],
      names: [],
      appClientIds: new Set<string>(),
    };
    if (a.pct !== null && Number.isFinite(a.pct)) {
      d.pct = Math.min(100, d.pct + a.pct);
      d.hasPct = true;
    }
    d.refs.push(a.id);
    if (!d.names.includes(a.clientName)) d.names.push(a.clientName);
    d.appClientIds.add(a.clientAppId);
    drafts.set(id, d);
  }

  const upserts: OrgAssignmentUpsert[] = [];
  const history: OrgHistoryEvent[] = [];
  let created = 0;
  let updated = 0;
  let confirmed = 0;

  for (const [id, d] of drafts) {
    const ex = existingById.get(id) ?? null;
    const lockedAll = [...d.appClientIds].every((c) => input.locks.has(c));
    const lockedBy = lockedAll ? [...d.appClientIds].map((c) => input.locks.get(c)).find(Boolean) ?? null : null;
    const status = deriveCapacityStatus(ex, lockedAll);
    const origin: OrgAssignmentOrigin = ex?.origin === 'declared' ? 'declared' : 'capacity';
    const row: OrgAssignmentUpsert = {
      id,
      employee_id: d.employeeId,
      client_id: d.clientId,
      solution_slug: d.solutionSlug,
      period,
      role_title: d.roleTitle,
      pct: d.hasPct ? Math.round(d.pct * 100) / 100 : null,
      origin,
      status,
      manager_email: d.managerEmail,
      confirmed_by: status === 'confirmed' ? ex?.confirmed_by ?? lockedBy ?? d.managerEmail : null,
      confirmed_at: status === 'confirmed' ? ex?.confirmed_at ?? now : null,
      closed_at: status === 'closed' ? ex?.closed_at ?? now : null,
      closed_reason: status === 'closed' ? ex?.closed_reason ?? 'manager' : null,
      source_system: 'hour-allocator',
      source_ref: d.refs.sort().join(','),
      source_client_name: d.names.join(' + '),
    };
    upserts.push(row);
    if (!ex) {
      created++;
      history.push({ assignment_id: id, by: SYNC_ACTOR, action: 'created', field: null, from_value: null, to_value: status });
      if (status === 'confirmed') confirmed++;
      continue;
    }
    const diffs: Array<[string, string | null, string | null]> = [];
    if ((ex.role_title ?? null) !== row.role_title) diffs.push(['role_title', ex.role_title ?? null, row.role_title]);
    if ((ex.pct ?? null) !== row.pct) diffs.push(['pct', ex.pct == null ? null : String(ex.pct), row.pct == null ? null : String(row.pct)]);
    if ((ex.manager_email ?? null) !== row.manager_email) diffs.push(['manager_email', ex.manager_email ?? null, row.manager_email]);
    if (diffs.length) updated++;
    for (const [field, from, to] of diffs) history.push({ assignment_id: id, by: SYNC_ACTOR, action: 'updated', field, from_value: from, to_value: to });
    if (ex.status !== status) {
      if (status === 'confirmed') {
        confirmed++;
        history.push({ assignment_id: id, by: SYNC_ACTOR, action: ex.status === 'closed' ? 'reopened' : 'confirmed', field: 'status', from_value: ex.status, to_value: status });
      } else if (ex.status === 'closed') {
        history.push({ assignment_id: id, by: SYNC_ACTOR, action: 'reopened', field: 'status', from_value: ex.status, to_value: status });
      }
    }
  }

  const closeIds: string[] = [];
  for (const ex of input.existing) {
    if (drafts.has(ex.id)) continue;
    if (shouldCloseMissing({ origin: ex.origin, status: ex.status, period }, [period])) {
      closeIds.push(ex.id);
      history.push({ assignment_id: ex.id, by: SYNC_ACTOR, action: 'closed', field: 'status', from_value: ex.status, to_value: 'closed' });
    }
  }

  const clientLocks = new Map<string, string | null>();
  for (const [clientId, appIds] of appIdsByClient) {
    if ([...appIds].every((id) => input.locks.has(id))) clientLocks.set(clientId, [...appIds].map((id) => input.locks.get(id)).find(Boolean) ?? null);
  }
  for (const [appId, name] of input.lockNames ?? []) {
    if (!input.locks.has(appId)) continue;
    const client = resolveClientId(name, input.clientIndex);
    if (client && !appIdsByClient.has(client.clientId)) clientLocks.set(client.clientId, input.locks.get(appId) ?? null);
  }

  const unresolvedClients = [...unresolved]
    .map(([name, people]) => ({ name, persons: people.size }))
    .sort((a, b) => b.persons - a.persons || a.name.localeCompare(b.name));

  return {
    upserts,
    history,
    closeIds,
    clientLocks,
    unresolvedClients,
    unresolvedSolutions: [...unresolvedSolutions].sort(),
    unmatchedPersons: unmatched.size,
    stats: {
      persons: new Set(upserts.map((u) => u.employee_id)).size,
      clients: new Set(upserts.map((u) => u.client_id)).size,
      assignments: upserts.length,
      created,
      updated,
      confirmed,
      closed: closeIds.length,
      unresolved_clients: unresolvedClients.length,
      unmatched_persons: unmatched.size,
    },
  };
}

// ─── Vigencia para lecturas ──────────────────────────────────────────────────

/**
 * Las asignaciones que valen HOY de un conjunto (mes anterior + en curso), con los locks del
 * mes en curso (`org.client_lock`): una del mes anterior cae cuando el cliente ya cerró el mes
 * en curso; las cerradas nunca cuentan. Si una persona tiene la misma asignación en los dos
 * meses queda UNA (la del mes en curso).
 */
export function selectCurrentAssignments<A extends { period: string; status: OrgAssignmentStatus; employee_id: number; client_id: string; solution_slug: string }>(
  assignments: readonly A[],
  lockedClientsCurrentMonth: ReadonlySet<string>,
  today: string = todayAR(),
): A[] {
  const cur = currentPeriod(today);
  const byKey = new Map<string, A>();
  for (const a of assignments) {
    if (!isAssignmentCurrent(a, { today, currentMonthLocked: lockedClientsCurrentMonth.has(a.client_id) })) continue;
    const key = `${a.employee_id}:${a.client_id}:${a.solution_slug}`;
    const prev = byKey.get(key);
    if (!prev || (a.period === cur && prev.period !== cur)) byKey.set(key, a);
  }
  return [...byKey.values()];
}

// ─── Escrituras del manager ──────────────────────────────────────────────────

export interface WriteAssignmentCheck {
  /** Quien escribe. */
  viewerEmail: string;
  viewerCanAdmin: boolean;
  viewerCanViewAll: boolean;
  /** La persona de la asignación. */
  targetEmail: string;
  /** Líder directo HOY en el Community Map (re-resuelto al escribir, no el de la pestaña abierta). */
  targetDirectLeaderEmail: string | null;
  /** Manager vigente que trajo el sync (team_members.manager_id del mes), si la fila lo tiene. */
  assignmentManagerEmail?: string | null;
}

/**
 * Quién puede confirmar, corregir, cerrar o declarar una asignación: el roster / admins, o el
 * líder DIRECTO de la persona (plan: "el subárbol hacia arriba ve pero no escribe"; lección del
 * Mapa de Talento 18/09: evalúa solo el líder directo). Nadie se escribe a sí mismo.
 */
export function canWriteAssignment(c: WriteAssignmentCheck): boolean {
  if (c.viewerCanAdmin || c.viewerCanViewAll) return true;
  const v = normalizeEmail(c.viewerEmail);
  if (!v || v === normalizeEmail(c.targetEmail)) return false;
  return normalizeEmail(c.targetDirectLeaderEmail) === v || normalizeEmail(c.assignmentManagerEmail) === v;
}

/** Lo que un manager puede corregir a mano: solo lo que NO viene de la app (ahí se corrige en la app y el sync lo trae). */
export function isManagerEditable(a: { origin: OrgAssignmentOrigin }): boolean {
  return a.origin !== 'capacity';
}

// ─── Agente de asignaciones (T3b): lo que el modelo propone se resuelve acá, no en el prompt ──

export interface AgentProposalRaw {
  person: string;
  client: string;
  solution?: string | null;
  pct?: number | null;
}
export interface AgentPerson {
  employee_id: number;
  full_name: string;
  email: string;
  solution_slug: string | null;
}
export interface AgentClient {
  client_id: string;
  display_name: string;
}
export interface AgentProposalResolved {
  employeeId: number;
  personName: string;
  personEmail: string;
  clientId: string;
  clientName: string;
  solutionSlug: string;
  pct: number | null;
}

/**
 * Candidatos para un texto libre que nombra a una persona (lo que escribió un manager o una
 * tool MCP): por mail exacto; si no, por tokens del nombre (`sharesNameTokens`), y si hay
 * varios, los de nombre exacto. Más de uno = ambiguo, lo decide el caller. Para resolver una
 * persona desde un dato estructurado (líder, miembro) va `community-index`, no esto.
 */
export function matchPeopleByText<P extends { email: string; full_name: string }>(text: string, people: readonly P[]): P[] {
  const q = text.trim();
  if (!q) return [];
  const byEmail = people.filter((p) => normalizeEmail(p.email) === normalizeEmail(q));
  if (byEmail.length) return byEmail;
  const byName = people.filter((p) => sharesNameTokens(q, p.full_name));
  if (byName.length <= 1) return byName;
  const exact = byName.filter((p) => foldKey(p.full_name) === foldKey(q));
  return exact.length ? exact : byName;
}

/**
 * Convierte lo que escribió el modelo (nombres libres) en ids del Org Service: persona por mail o
 * por tokens del nombre (una sola coincidencia, si no `person_ambiguous`), cliente por el índice
 * de nombres (exacto o familia), solución por slug o nombre (o la principal de la persona). Lo
 * que no resuelve se devuelve con motivo; nada se inventa.
 */
export function resolveAgentProposals(
  raw: readonly AgentProposalRaw[],
  ctx: { people: readonly AgentPerson[]; clients: readonly AgentClient[]; /** quien pregunta: nadie se propone a sí mismo (ni el roster) */ selfEmail?: string | null },
): { ok: AgentProposalResolved[]; rejected: OrgAgentRejected[] } {
  const self = normalizeEmail(ctx.selfEmail);
  const clientIndex = buildClientNameIndex(ctx.clients.map((c) => ({ id: c.client_id, displayName: c.display_name })));
  const ok: AgentProposalResolved[] = [];
  const rejected: OrgAgentRejected[] = [];
  const seen = new Set<string>();
  for (const r of raw) {
    const personText = (r.person ?? '').trim();
    const clientText = (r.client ?? '').trim();
    const candidates = matchPeopleByText(personText, ctx.people);
    if (!candidates.length) {
      rejected.push({ person: personText, client: clientText, reason: 'person' });
      continue;
    }
    if (candidates.length > 1) {
      rejected.push({ person: personText, client: clientText, reason: 'person_ambiguous' });
      continue;
    }
    const person = candidates[0];
    if (self && normalizeEmail(person.email) === self) {
      rejected.push({ person: personText, client: clientText, reason: 'self' });
      continue;
    }
    const client = clientText ? resolveClientId(clientText, clientIndex) : null;
    if (!client) {
      rejected.push({ person: personText, client: clientText, reason: 'client' });
      continue;
    }
    const solText = (r.solution ?? '').trim();
    const solution = solText ? solutionByText(solText) : solutionBySlug(person.solution_slug);
    if (!solution || !solution.worksWithClients) {
      rejected.push({ person: personText, client: clientText, reason: 'solution' });
      continue;
    }
    const pct = r.pct === null || r.pct === undefined ? null : Number(r.pct);
    if (pct !== null && (!Number.isFinite(pct) || pct < 0 || pct > 100)) {
      rejected.push({ person: personText, client: clientText, reason: 'pct' });
      continue;
    }
    const key = `${person.employee_id}:${client.clientId}:${solution.slug}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const clientName = ctx.clients.find((c) => c.client_id === client.clientId)?.display_name ?? client.clientId;
    ok.push({ employeeId: person.employee_id, personName: person.full_name, personEmail: person.email, clientId: client.clientId, clientName, solutionSlug: solution.slug, pct });
  }
  return { ok, rejected };
}

// ─── Recordatorio semanal (T3b) ──────────────────────────────────────────────

/**
 * Sugeridas vigentes agrupadas por quién las confirma: el manager que trajo el sync, o el líder
 * directo de la persona en el Community Map si la fila no tiene manager. Sin manager posible, la
 * fila no se avisa a nadie (queda para el roster en la tab Salud).
 */
export function groupPendingByManager<A extends { status: OrgAssignmentStatus; manager_email: string | null; email: string }>(
  assignments: readonly A[],
  leaderOf: (personEmail: string) => string | null,
): Map<string, A[]> {
  const out = new Map<string, A[]>();
  for (const a of assignments) {
    if (a.status !== 'suggested') continue;
    const manager = normalizeEmail(a.manager_email) || normalizeEmail(leaderOf(a.email));
    if (!manager) continue;
    const list = out.get(manager) ?? [];
    list.push(a);
    out.set(manager, list);
  }
  return out;
}

/** El DM semanal al manager: cuántas, tres ejemplos y el link al panel. Texto fijo en español (es un aviso operativo). */
export function renderOrgReminderMessage(items: readonly { full_name: string; display_name: string }[], platformUrl: string): string {
  const n = items.length;
  const ejemplos = items.slice(0, 3).map((a) => `• ${a.full_name} → ${a.display_name}`).join('\n');
  const resto = n > 3 ? `\n… y ${n - 3} más.` : '';
  return [
    `Hola, tenés ${n} ${n === 1 ? 'asignación' : 'asignaciones'} de tu equipo sin confirmar en el Org Service (quién trabaja con qué cliente):`,
    ejemplos + resto,
    `Confirmalas o corregilas acá: ${platformUrl}/org?tab=team — si alguna no corresponde, cerrala y el sync no la vuelve a abrir.`,
  ].join('\n\n');
}
